import React, { useState } from 'react';
import { FileTextIcon, PrinterIcon, DownloadIcon, EyeIcon, CheckCircleIcon, ClipboardCheckIcon, BuildingIcon, TruckIcon, PackageIcon, CalendarIcon, CircleDollarSignIcon, MapPinIcon } from 'lucide-react';
import { QuotePreview } from './QuotePreview';
export function QuoteReviewStep({
  quoteData,
  quoteType
}) {
  const [showPreview, setShowPreview] = useState(false);
  // Generate a random quote number
  const quoteNumber = `Q${Math.floor(Math.random() * 100000).toString().padStart(5, '0')}`;
  // Calculate totals
  const calculateSubtotal = () => {
    if (quoteType === 'Pivot') {
      return quoteData.pivots.reduce((sum, pivot) => sum + (pivot.price || 0), 0);
    } else if (quoteType === 'Spare Parts') {
      return quoteData.parts.reduce((sum, part) => sum + (part.quantity * part.unitPrice || 0), 0);
    } else {
      return quoteData.items.reduce((sum, item) => sum + (item.price || 0), 0);
    }
  };
  const subtotal = calculateSubtotal();
  const discount = quoteData.quoteDetails?.discount || 0;
  const discountAmount = subtotal * (discount / 100);
  const shippingTotal = quoteData.shipping?.shipments?.reduce((sum, shipment) => sum + (shipment.totalPrice || 0), 0) || 0;
  const total = subtotal - discountAmount + shippingTotal;
  return <div className="space-y-8" data-id="element-4963">
      <div className="text-white text-xl font-medium mb-6" data-id="element-4964">Quote Review</div>
      {/* Summary Card */}
      <div className="bg-slate-800/50 rounded-xl p-5 border border-blue-800/30" data-id="element-4965">
        <div className="flex items-center mb-4" data-id="element-4966">
          <ClipboardCheckIcon size={20} className="text-green-400 mr-2" data-id="element-4967" />
          <h3 className="text-white font-medium" data-id="element-4968">Quote Summary</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6" data-id="element-4969">
          <div className="bg-slate-900/30 rounded-lg p-3" data-id="element-4970">
            <div className="text-sm text-blue-300 mb-1" data-id="element-4971">Quote Number</div>
            <div className="text-white font-medium" data-id="element-4972">{quoteNumber}</div>
          </div>
          <div className="bg-slate-900/30 rounded-lg p-3" data-id="element-4973">
            <div className="text-sm text-blue-300 mb-1" data-id="element-4974">Quote Type</div>
            <div className="text-white font-medium" data-id="element-4975">{quoteType} Quote</div>
          </div>
          <div className="bg-slate-900/30 rounded-lg p-3" data-id="element-4976">
            <div className="text-sm text-blue-300 mb-1" data-id="element-4977">Issue Date</div>
            <div className="text-white font-medium" data-id="element-4978">
              {new Date().toLocaleDateString()}
            </div>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6" data-id="element-4979">
          {/* Customer Information */}
          <div data-id="element-4980">
            <div className="flex items-center mb-3" data-id="element-4981">
              <BuildingIcon size={16} className="text-blue-400 mr-2" data-id="element-4982" />
              <h4 className="text-blue-300 font-medium text-sm" data-id="element-4983">
                Customer Information
              </h4>
            </div>
            <div className="bg-slate-900/30 rounded-lg p-3 mb-3" data-id="element-4984">
              <div className="text-xs text-blue-300 mb-1" data-id="element-4985">Dealer</div>
              <div className="text-white" data-id="element-4986">
                {quoteData.customerInfo?.dealerName || 'Not specified'}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3" data-id="element-4987">
              <div className="bg-slate-900/30 rounded-lg p-3" data-id="element-4988">
                <div className="text-xs text-blue-300 mb-1" data-id="element-4989">Bill To</div>
                <div className="text-white" data-id="element-4990">
                  {quoteData.customerInfo?.billToCompany || 'Not specified'}
                </div>
                <div className="text-sm text-slate-400 mt-1" data-id="element-4991">
                  {quoteData.customerInfo?.billToCountry || ''}
                </div>
              </div>
              <div className="bg-slate-900/30 rounded-lg p-3" data-id="element-4992">
                <div className="text-xs text-blue-300 mb-1" data-id="element-4993">Ship To</div>
                <div className="text-white" data-id="element-4994">
                  {quoteData.customerInfo?.shipToCompany || 'Not specified'}
                </div>
                <div className="text-sm text-slate-400 mt-1" data-id="element-4995">
                  {quoteData.customerInfo?.shipToCountry || ''}
                </div>
              </div>
            </div>
          </div>
          {/* Quote Details */}
          <div data-id="element-4996">
            <div className="flex items-center mb-3" data-id="element-4997">
              <FileTextIcon size={16} className="text-blue-400 mr-2" data-id="element-4998" />
              <h4 className="text-blue-300 font-medium text-sm" data-id="element-4999">
                Quote Details
              </h4>
            </div>
            <div className="grid grid-cols-2 gap-3 mb-3" data-id="element-5000">
              <div className="bg-slate-900/30 rounded-lg p-3" data-id="element-5001">
                <div className="text-xs text-blue-300 mb-1" data-id="element-5002">Valid Until</div>
                <div className="text-white" data-id="element-5003">
                  {quoteData.shipping?.quoteValidUntil || 'Not specified'}
                </div>
              </div>
              <div className="bg-slate-900/30 rounded-lg p-3" data-id="element-5004">
                <div className="text-xs text-blue-300 mb-1" data-id="element-5005">Delivery Date</div>
                <div className="text-white" data-id="element-5006">
                  {quoteData.shipping?.desiredDeliveryDate || 'Not specified'}
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3" data-id="element-5007">
              <div className="bg-slate-900/30 rounded-lg p-3" data-id="element-5008">
                <div className="text-xs text-blue-300 mb-1" data-id="element-5009">Payment Terms</div>
                <div className="text-white" data-id="element-5010">
                  {quoteData.shipping?.paymentTerms || 'Not specified'}
                </div>
              </div>
              <div className="bg-slate-900/30 rounded-lg p-3" data-id="element-5011">
                <div className="text-xs text-blue-300 mb-1" data-id="element-5012">Incoterm</div>
                <div className="text-white" data-id="element-5013">
                  {quoteData.shipping?.incoterm || 'Not specified'}
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* Items Summary */}
        <div className="mb-6" data-id="element-5014">
          <div className="flex items-center mb-3" data-id="element-5015">
            <PackageIcon size={16} className="text-blue-400 mr-2" data-id="element-5016" />
            <h4 className="text-blue-300 font-medium text-sm" data-id="element-5017">Items Summary</h4>
          </div>
          <div className="bg-slate-900/30 rounded-lg p-4" data-id="element-5018">
            {quoteType === 'Pivot' && <div className="text-white" data-id="element-5019">
                {quoteData.pivots.length} Pivot Machine
                {quoteData.pivots.length !== 1 ? 's' : ''}
              </div>}
            {quoteType === 'Spare Parts' && <div className="text-white" data-id="element-5020">
                {quoteData.parts.length} Spare Part
                {quoteData.parts.length !== 1 ? 's' : ''}
              </div>}
            {quoteType === 'Project' && <div className="text-white" data-id="element-5021">
                {quoteData.items.length} Project Item
                {quoteData.items.length !== 1 ? 's' : ''}
              </div>}
          </div>
        </div>
        {/* Financial Summary */}
        <div data-id="element-5022">
          <div className="flex items-center mb-3" data-id="element-5023">
            <CircleDollarSignIcon size={16} className="text-blue-400 mr-2" data-id="element-5024" />
            <h4 className="text-blue-300 font-medium text-sm" data-id="element-5025">
              Financial Summary
            </h4>
          </div>
          <div className="bg-slate-900/30 rounded-lg p-4" data-id="element-5026">
            <div className="space-y-2 mb-4" data-id="element-5027">
              <div className="flex justify-between" data-id="element-5028">
                <span className="text-blue-300" data-id="element-5029">Subtotal:</span>
                <span className="text-white" data-id="element-5030">
                  {quoteData.shipping?.currency || '$'} {subtotal.toFixed(2)}
                </span>
              </div>
              {discount > 0 && <div className="flex justify-between" data-id="element-5031">
                  <span className="text-blue-300" data-id="element-5032">Discount ({discount}%):</span>
                  <span className="text-green-400" data-id="element-5033">
                    - {quoteData.shipping?.currency || '$'}{' '}
                    {discountAmount.toFixed(2)}
                  </span>
                </div>}
              {shippingTotal > 0 && <div className="flex justify-between" data-id="element-5034">
                  <span className="text-blue-300" data-id="element-5035">Shipping:</span>
                  <span className="text-white" data-id="element-5036">
                    {quoteData.shipping?.currency || '$'}{' '}
                    {shippingTotal.toFixed(2)}
                  </span>
                </div>}
            </div>
            <div className="flex justify-between pt-3 border-t border-blue-800/30" data-id="element-5037">
              <span className="text-white font-medium" data-id="element-5038">Total:</span>
              <span className="text-xl text-white font-bold" data-id="element-5039">
                {quoteData.shipping?.currency || '$'} {total.toFixed(2)}
              </span>
            </div>
          </div>
        </div>
      </div>
      {/* Action Buttons */}
      <div className="flex flex-wrap gap-4 justify-center" data-id="element-5040">
        <button onClick={() => setShowPreview(true)} className="flex items-center px-4 py-2.5 rounded-lg bg-blue-600/20 text-blue-300 hover:bg-blue-600/40 hover:text-white transition-colors" data-id="element-5041">
          <EyeIcon size={18} className="mr-2" data-id="element-5042" />
          Preview Quote
        </button>
        <button onClick={() => console.log('Print quote')} className="flex items-center px-4 py-2.5 rounded-lg bg-blue-600/20 text-blue-300 hover:bg-blue-600/40 hover:text-white transition-colors" data-id="element-5043">
          <PrinterIcon size={18} className="mr-2" data-id="element-5044" />
          Print Quote
        </button>
        <button onClick={() => console.log('Download PDF')} className="flex items-center px-4 py-2.5 rounded-lg bg-blue-600/20 text-blue-300 hover:bg-blue-600/40 hover:text-white transition-colors" data-id="element-5045">
          <DownloadIcon size={18} className="mr-2" data-id="element-5046" />
          Download PDF
        </button>
      </div>
      {/* Preview Modal */}
      {showPreview && <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" data-id="element-5047">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl max-h-[90vh] overflow-auto" data-id="element-5048">
            <div className="sticky top-0 bg-white p-4 border-b flex justify-between items-center" data-id="element-5049">
              <h3 className="text-lg font-medium text-gray-900" data-id="element-5050">
                Quote Preview
              </h3>
              <button onClick={() => setShowPreview(false)} className="text-gray-400 hover:text-gray-500" data-id="element-5051">
                <span className="text-2xl" data-id="element-5052">&times;</span>
              </button>
            </div>
            <div className="p-6" data-id="element-5053">
              <QuotePreview quoteData={quoteData} quoteType={quoteType} quoteNumber={quoteNumber} data-id="element-5054" />
            </div>
            <div className="sticky bottom-0 bg-gray-50 p-4 border-t flex justify-end" data-id="element-5055">
              <button onClick={() => setShowPreview(false)} className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg mr-3 hover:bg-gray-300 transition-colors" data-id="element-5056">
                Close
              </button>
              <button onClick={() => console.log('Download PDF')} className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors" data-id="element-5057">
                <DownloadIcon size={16} className="mr-2" data-id="element-5058" />
                Download PDF
              </button>
            </div>
          </div>
        </div>}
    </div>;
}